<!-- src/components/app-sidebar/nav-team-add-step4-staff.vue -->
<!-- Step 4: Add Staff Members - Simple text input for newline-separated names -->
<!-- This step collects staff names for call tracking, one name per line -->
<!-- RELEVANT FILES: nav-team-add.vue, organization-config.ts -->

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { Users } from 'lucide-vue-next';
import { useForm } from 'vee-validate';
import { z } from 'zod';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';

interface Props {
  isSubmitting?: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
  submit: [data: string];
  skip: [];
}>();

// Simple validation for staff names string
const staffSchema = z.object({
  staff: z.string().max(500, 'Staff names list is too long').optional().or(z.literal('')),
});

const formSchema = toTypedSchema(staffSchema);

const { handleSubmit } = useForm({
  validationSchema: formSchema,
  initialValues: {
    staff: '',
  },
});

// Form submission
const onFormSubmit = handleSubmit(data => {
  emit('submit', data.staff || '');
});
</script>

<template>
  <form class="space-y-6" @submit="onFormSubmit">
    <div class="text-center space-y-2">
      <div class="flex items-center justify-center gap-2">
        <Users class="h-5 w-5 text-primary" />
        <h3 class="text-lg font-semibold">Add Staff Members</h3>
      </div>
      <p class="text-sm text-muted-foreground">
        Enter your team members' names (one per line) for call tracking and performance insights.
      </p>
    </div>

    <!-- Important Notice - Blue highlight box -->
    <div class="rounded-lg bg-blue-50 border border-blue-200 p-4">
      <div class="flex gap-3">
        <div class="flex-shrink-0 mt-0.5">
          <div class="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-xs">
            💡
          </div>
        </div>
        <div class="flex-1">
          <p class="text-sm font-medium text-blue-900 mb-1">Optional but Recommended</p>
          <p class="text-sm text-blue-700">
            Adding staff names enables individual performance tracking and helps identify coaching
            opportunities for each team member.
          </p>
        </div>
      </div>
    </div>

    <!-- Staff Names Input -->
    <FormField v-slot="{ componentField }" name="staff">
      <FormItem>
        <FormLabel class="text-base"> Staff Names </FormLabel>
        <FormControl>
          <Textarea
            v-bind="componentField"
            placeholder="max&#10;peter&#10;sarah&#10;john"
            class="resize-none"
            rows="4"
          />
        </FormControl>
        <FormDescription class="text-xs">
          Enter staff member names, one per line. Press Enter after each name.
        </FormDescription>
        <FormMessage />
      </FormItem>
    </FormField>

    <!-- Helper Info - Lighter style to provide supporting information -->
    <div class="rounded-lg border border-border bg-card p-4">
      <div class="flex gap-3">
        <div class="flex-shrink-0 mt-0.5">
          <div class="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs">
            ℹ️
          </div>
        </div>
        <div class="flex-1">
          <p class="text-sm font-medium text-foreground mb-1">How staff tracking works</p>
          <p class="text-sm text-muted-foreground">
            We'll use these names to match with call recordings and provide individual performance
            insights. You can always update this list later in Settings.
          </p>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex justify-between items-center pt-4 border-t">
      <UiButton type="button" variant="outline" :disabled="isSubmitting" @click="emit('skip')">
        Skip for Now
      </UiButton>
      <UiButton type="submit" :loading="isSubmitting" :disabled="isSubmitting">
        Complete Setup
      </UiButton>
    </div>
  </form>
</template>
